/**
 * 🎯 COMPONENTE MODERNHERO - SECCIÓN PRINCIPAL DE LA PÁGINA
 * ============================================================
 * 
 * EXPLICACIÓN PARA UN NIÑO:
 * Este componente es como la "portada" de un libro. Es lo primero que ves
 * cuando entras a la página web. Tiene:
 * - Fotos que cambian solas (como un álbum de fotos automático)
 * - Un reloj cuenta regresiva (como cuando esperamos Año Nuevo)
 * - Un formulario para registrarse
 * - Botones para hacer clic
 * 
 * COMPONENTES QUE USA:
 * - React: La biblioteca que hace que todo funcione
 * - useState: Como una caja donde guardamos información
 * - useEffect: Como un ayudante que hace tareas cuando algo cambia
 */

// 📦 IMPORTACIONES - Traemos las herramientas que necesitamos
import React, { useState, useEffect } from 'react'; // React y sus herramientas mágicas
import { ArrowRight, Play } from 'lucide-react'; // Iconos bonitos (flechas y play)
import { Button } from '../ui/button'; // Un botón especial que ya está diseñado
import { useNavigate } from 'react-router-dom'; // Para navegar entre páginas
import { api } from '../../services/api'; // Para obtener datos del servidor

/**
 * 🏗️ COMPONENTE PRINCIPAL
 * Este es como la receta completa de cómo hacer el Hero
 */
const ModernHero = () => {
  const navigate = useNavigate(); // Herramienta para ir a otras páginas

  // ======================
  // 📦 CAJAS DE INFORMACIÓN (ESTADOS)
  // ======================
  // Imagina que cada "useState" es como una caja donde guardamos algo importante

  // Caja 1: Guardamos qué tan abajo hemos hecho scroll (desplazamiento)
  const [scrollY, setScrollY] = useState(0);

  // Caja 2: Guardamos qué foto estamos mostrando (0 = primera foto, 1 = segunda, etc.)
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Caja 3: Guardamos información del próximo remate
  const [remateData, setRemateData] = useState(null);

  // Caja 4: Guardamos el tiempo del contador regresivo
  const [timeLeft, setTimeLeft] = useState({
    days: 0,      // Días restantes
    hours: 0,     // Horas restantes
    minutes: 0,   // Minutos restantes
    seconds: 0    // Segundos restantes
  });

  // ======================
  // 🖼️ LISTA DE FOTOS PARA EL CARRUSEL
  // ======================
  // Esto es como tener un álbum de fotos. Cada dirección web (URL) es una foto diferente
  const heroImages = [
    'https://customer-assets.emergentagent.com/job_plaza-rural-modern/artifacts/uk75g78l_fondo.jpg', // Foto 1: Atardecer con molino
    'https://customer-assets.emergentagent.com/job_plaza-rural-modern/artifacts/8sa88la6_Ariana%20Munch%201.jpg', // Foto 2: Vaca con caravana
    'https://customer-assets.emergentagent.com/job_plaza-rural-modern/artifacts/6qmken8t_tapaNacionalHereford.jpg'  // Foto 3: Ovejas
  ];

  // ======================
  // 🎬 EFECTO 1: CARGAR DATOS DEL PRÓXIMO REMATE
  // ======================
  /*
   * ¿Qué hace esto?
   * Consulta al servidor para obtener información del próximo remate
   * (número, estado, fecha) y la actualiza cada 5 minutos.
   */
  useEffect(() => {
    console.log('Fetching próximo remate...');
    const fetchProximoRemate = async () => {
      try {
        const data = await api.getProximoRemate();
        console.log('[ModernHero] Próximo remate:', data);
        setRemateData(data);
      } catch (error) {
        console.error('[ModernHero] Error loading próximo remate:', error);
      }
    };

    fetchProximoRemate();
    // Recargar cada 5 minutos para actualizar
    const interval = setInterval(fetchProximoRemate, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // ======================
  // 🎬 EFECTO 2: DETECTAR CUANDO HACEMOS SCROLL
  // ======================
  /*
   * ¿Qué hace esto?
   * Imagina que estás leyendo un libro y quiero saber en qué página estás.
   * Este código hace exactamente eso, pero con el scroll de la página web.
   * Cuando haces scroll (mueves la página hacia abajo), guardamos cuánto te moviste.
   */
  useEffect(() => {
    // Función que se ejecuta cada vez que hacemos scroll
    const handleScroll = () => {
      setScrollY(window.scrollY); // Guardamos la nueva posición del scroll
    };

    // Le decimos al navegador: "Cuando hagan scroll, ejecuta handleScroll"
    window.addEventListener('scroll', handleScroll);

    // Cuando el componente se va (como cuando cambias de página), limpiamos
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // Los [] significan: "Solo hazlo una vez cuando cargue la página"

  // ======================
  // 🎬 EFECTO 3: CAMBIAR LAS FOTOS AUTOMÁTICAMENTE
  // ======================
  /*
   * ¿Qué hace esto?
   * Es como un álbum de fotos que pasa las páginas solo.
   * Cada 4 segundos, cambia a la siguiente foto.
   * Cuando llega a la última foto, vuelve a la primera.
   */
  useEffect(() => {
    // setInterval es como un temporizador que hace algo cada cierto tiempo
    const interval = setInterval(() => {
      // Cambiamos a la siguiente foto
      // El % (módulo) hace que vuelva a 0 cuando llega al final
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000); // 4000 milisegundos = 4 segundos

    // Cuando el componente se va, detenemos el temporizador
    return () => clearInterval(interval);
  }, []); // Solo configuramos el temporizador una vez

  // ======================
  // 🎬 EFECTO 4: CALCULAR TIEMPO RESTANTE DINÁMICAMENTE
  // ======================
  /*
   * ¿Qué hace esto?
   * Calcula cuánto tiempo falta para el próximo remate basándose en
   * los datos recibidos del servidor. Si el remate está en curso,
   * muestra todo en ceros.
   */
  useEffect(() => {
    if (!remateData) return;

    // Si está en curso, mantener todo en ceros
    if (remateData.en_curso) {
      setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    // Si no hay fecha, mantener en ceros
    if (!remateData.fecha_proxima) {
      setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    // Calcular diferencia de tiempo
    const calculateTimeLeft = () => {
      const targetDate = new Date(remateData.fecha_proxima);
      const now = new Date();
      const difference = targetDate - now;

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((difference / 1000 / 60) % 60);
      const seconds = Math.floor((difference / 1000) % 60);

      return { days, hours, minutes, seconds };
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [remateData]);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Carrusel de Imágenes con transición DRAMÁTICA */}
      <div className="absolute inset-0">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className="absolute inset-0 transition-all duration-2000"
            style={{
              backgroundImage: `url('${image}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: index === currentImageIndex ? 1 : 0,
              transform: index === currentImageIndex
                ? `scale(1) translateY(${scrollY * 0.5}px)`
                : 'scale(1.2)',
              transition: 'opacity 2s ease-in-out, transform 2s ease-in-out'
            }}
          />
        ))}
      </div>

      {/* Overlay corporativo y profesional */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/60 to-green-900/60 z-10"></div>

      {/* Efecto de vignette sutil */}
      <div className="absolute inset-0 z-10" style={{
        background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.3) 100%)'
      }}></div>

      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 py-20 md:py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left Content con animaciones dramáticas */}
          <div className="text-white space-y-4 md:space-y-6">
            {/* Badge EN VIVO profesional - solo mostrar cuando está en curso */}
            {remateData?.en_curso && (
              <div className="inline-flex items-center space-x-2 bg-black/40 backdrop-blur-sm rounded-full px-5 py-2 border border-red-500/50 animate-slide-in-left">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
                </span>
                <span className="text-sm font-semibold">REMATE EN VIVO</span>
              </div>
            )}

            {/* Título con reveal dramático - TAMAÑOS MÁS DELICADOS */}
            <div className="space-y-2 md:space-y-3 animate-clip-reveal" style={{ animationDelay: '0.3s' }}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="block animate-slide-in-left" style={{ animationDelay: '0.5s' }}>Remate</span>
                <span className="block bg-gradient-to-r from-green-400 via-yellow-300 to-green-400 bg-clip-text text-transparent text-5xl md:text-6xl lg:text-7xl animate-slide-in-right" style={{ animationDelay: '0.7s' }}>
                  {/* Caso especial: Remates 315 y 316 se muestran juntos */}
                  {(remateData?.numero === 315 || remateData?.numero === 316)
                    ? '#315 y #316'
                    : `#${remateData?.numero || '...'}`
                  }
                </span>
              </h1>
              {/* Logo Agro en Punta para remates 315 y 316 */}
              {(remateData?.numero === 315 || remateData?.numero === 316) && (
                <div className="animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg md:rounded-xl p-2 md:p-3 border border-white/20 inline-block">
                    <img
                      src="/web/socios/agro_en_punta.svg"
                      alt="Agro en Punta"
                      className="h-10 md:h-14 lg:h-16 w-auto"
                    />
                  </div>
                </div>
              )}
              <p className="text-base md:text-lg text-gray-200 max-w-lg animate-fade-in-up" style={{ animationDelay: '0.9s' }}>
                Más oportunidades, mejores negocios
              </p>
            </div>

            {/* Countdown profesional */}
            <div className="grid grid-cols-4 gap-2 md:gap-4" style={{ animationDelay: '1.1s' }}>
              {[
                { value: timeLeft.days, label: 'Días' },
                { value: timeLeft.hours, label: 'Horas' },
                { value: timeLeft.minutes, label: 'Min' },
                { value: timeLeft.seconds, label: 'Seg' }
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-black/40 backdrop-blur-sm border border-white/20 rounded-lg md:rounded-xl p-2 md:p-4 text-center animate-fade-in-up"
                  style={{ animationDelay: `${1.2 + index * 0.1}s` }}
                >
                  <div className="text-2xl md:text-3xl font-bold text-yellow-400">
                    {String(item.value).padStart(2, '0')}
                  </div>
                  <div className="text-[10px] md:text-xs text-gray-300 mt-0.5 md:mt-1 uppercase tracking-wide">{item.label}</div>
                </div>
              ))}
            </div>

            {/* Botones profesionales */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 animate-fade-in-up" style={{ animationDelay: '1.5s' }}>
              <Button
                onClick={() => {
                  if (remateData?.numero) {
                    navigate(`/lotes?remate=${remateData.numero}`);
                  } else {
                    navigate('/lotes');
                  }
                }}
                className="bg-green-600 hover:bg-green-700 text-white px-6 md:px-8 py-4 md:py-5 text-sm md:text-base font-semibold rounded-lg shadow-lg transition-all duration-300 group"
              >
                <span className="flex items-center justify-center">
                  Ver Lotes
                  <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </Button>
              {remateData?.en_curso && (
                <Button className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white px-6 md:px-8 py-4 md:py-5 text-sm md:text-base font-semibold rounded-lg border border-white/30 transition-all duration-300 group">
                  <span className="flex items-center justify-center">
                    <Play className="mr-2 w-4 h-4 md:w-5 md:h-5" />
                    Ver Transmisión
                  </span>
                </Button>
              )}
            </div>

          </div>

          {/* Formulario profesional */}
          <div className="animate-fade-in-up mt-8 lg:mt-0" style={{ animationDelay: '1s' }}>
            <div className="bg-white/10 backdrop-blur-xl rounded-xl p-4 md:p-6 border border-white/20 shadow-2xl">
              <h3 className="text-xl md:text-2xl font-bold text-white text-center mb-4 md:mb-6">Regístrate aquí</h3>
              <form className="space-y-3 md:space-y-4">
                {/* Social Login Buttons */}
                <div className="space-y-3">
                  {/* <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      className="flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-medium py-2.5 md:py-3 rounded-lg border border-gray-200 transition-all duration-300 text-xs md:text-sm group"
                    >
                      <svg className="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                      </svg>
                      <span className="hidden sm:inline">Google</span>
                    </button>
                    <button
                      type="button"
                      className="flex items-center justify-center gap-2 bg-[#1877F2] hover:bg-[#166FE5] text-white font-medium py-2.5 md:py-3 rounded-lg transition-all duration-300 text-xs md:text-sm group"
                    >
                      <svg className="w-4 h-4 md:w-5 md:h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                      <span className="hidden sm:inline">Facebook</span>
                    </button>
                  </div> */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-white/20"></div>
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-2 bg-white/10 text-white/70">Completa el formulario</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <input
                    type="text"
                    placeholder="Nombre"
                    className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-3 md:px-4 py-2 md:py-3 text-white placeholder-white/60 focus:outline-none focus:border-green-400 transition-all text-xs md:text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Apellido"
                    className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-3 md:px-4 py-2 md:py-3 text-white placeholder-white/60 focus:outline-none focus:border-green-400 transition-all text-xs md:text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <input
                    type="text"
                    placeholder="Cédula"
                    className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-3 md:px-4 py-2 md:py-3 text-white placeholder-white/60 focus:outline-none focus:border-green-400 transition-all text-xs md:text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Departamento"
                    className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-3 md:px-4 py-2 md:py-3 text-white placeholder-white/60 focus:outline-none focus:border-green-400 transition-all text-xs md:text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <input
                    type="text"
                    placeholder="Localidad"
                    className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-3 md:px-4 py-2 md:py-3 text-white placeholder-white/60 focus:outline-none focus:border-green-400 transition-all text-xs md:text-sm"
                  />
                  <input
                    type="email"
                    placeholder="Correo"
                    className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-3 md:px-4 py-2 md:py-3 text-white placeholder-white/60 focus:outline-none focus:border-green-400 transition-all text-xs md:text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <select className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-2 md:px-3 py-2 md:py-3 text-white focus:outline-none focus:border-green-400 transition-all w-24 md:w-28 text-xs md:text-sm">
                    <option value="+598">🇺🇾 +598</option>
                    <option value="+54">🇦🇷 +54</option>
                    <option value="+55">🇧🇷 +55</option>
                  </select>
                  <input
                    type="tel"
                    placeholder="Teléfono"
                    className="flex-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-3 md:px-4 py-2 md:py-3 text-white placeholder-white/60 focus:outline-none focus:border-green-400 transition-all text-xs md:text-sm"
                  />
                </div>
                <input
                  type="text"
                  placeholder="Actividad"
                  className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-3 md:px-4 py-2 md:py-3 text-white placeholder-white/60 focus:outline-none focus:border-green-400 transition-all w-full text-xs md:text-sm"
                />
                <button
                  type="submit"
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 md:py-4 rounded-lg shadow-lg transition-all duration-300 text-sm md:text-base"
                >
                  REGISTRARME AHORA
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/70 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-white rounded-full animate-pulse"></div>
        </div>
      </div>
    </section>
  );
};

export default ModernHero;